import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

import InputSearch from '@cobliteam/ui/form/InputSearch';

import type { Driver } from '@cobliteam/api-lib/src/dashboard/driver';

import useDriver from '@cobliteam/hooks/src/useEntity/useDriver';

import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import DriverItem from './components/DriverItem';
import DriverTitle from './components/DriverTitle';

interface MultiSelectDriversProps {
  size?: 'sm' | 'md';
  isOpen?: boolean;
  selectedDrivers: Driver[];
  titleComponent?: React.ReactNode;
  onClick?: () => void;
  onChange: (drivers: Driver[]) => void;
}

const MultiSelectDrivers = ({ size = 'md', isOpen, titleComponent, selectedDrivers, onClick: onDropdownClick, onChange }: MultiSelectDriversProps) => {
  const [forceClosed, setForceClosed] = useState(false);
  const [isFetchEnabled, setFetchEnabled] = useState(false);
  const [draft, setDraft] = useState(selectedDrivers || []);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const { data: drivers, isFetching } = useDriver().getDrivers({
    options: {
      enabled: isFetchEnabled,
    },
  });

  const hasSameElements = selectedDrivers?.length === draft?.length && selectedDrivers.every((value) => draft.includes(value));

  const onClick = useCallback(() => {
    onDropdownClick?.();
    setFetchEnabled(true);
    setDraft(selectedDrivers || []);
  }, [onDropdownClick, selectedDrivers]);

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft(selectedDrivers || []);
    setSearch(undefined);
  }, [selectedDrivers]);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => {
    setForceClosed(!isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const toggleItem = useCallback(
    (dataItem: Driver) => {
      const newItemDraft = Array.from(draft);
      const existItem = newItemDraft.some((draftItem) => draftItem.id === dataItem.id);
      if (existItem) newItemDraft.splice(newItemDraft.map((v) => v.id).indexOf(dataItem.id), 1);
      if (!existItem) newItemDraft.push(dataItem);
      setDraft(newItemDraft);
    },
    [draft, setDraft]
  );

  const sortByName = (a: Driver, b: Driver) => {
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  };

  const sortBySelected = useCallback(
    (a: Driver, b: Driver) => {
      if (selectedDrivers.find((g) => g.id === a.id) && !selectedDrivers.find((g) => g.id === b.id)) return -1;
      if (!selectedDrivers.find((g) => g.id === a.id) && selectedDrivers.find((g) => g.id === b.id)) return 1;
      return 0;
    },
    [selectedDrivers]
  );

  const dataFiltered = drivers
    ?.filter((driver: Driver) => {
      if (!search) return true;
      return driver.name.toLowerCase().includes(search.toLowerCase());
    })
    .sort(sortByName)
    .sort(sortBySelected);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-5'>
            <Button variant='text' size='sm' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              Limpar seleção
            </Button>
          </Dropdown.Header>
          <Dropdown.Search className='px-5'>
            <InputSearch placeholder='Procure pelo nome' value={search} autoFocus debounceInMillis={500} onChange={setSearch} className='w-full' />
          </Dropdown.Search>
          <Dropdown.Body>
            <Dropdown.List
              heightList={300}
              heightItem={32}
              data={dataFiltered || []}
              isLoading={isFetching}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: Driver) => {
                const checked = draft.findIndex((d) => d.id === item.id) !== -1;
                return <DriverItem data={item} onChange={() => toggleItem(item)} checked={checked} />;
              }}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-5'>
              <Button variant='text' full size='sm' onClick={handleClose}>
                Cancelar
              </Button>
              <Button size='sm' full onClick={handleApply} disabled={hasSameElements}>
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <div>{titleComponent || <DriverTitle size={size} selectedDrivers={selectedDrivers} />}</div>
    </Dropdown>
  );
};

export default MultiSelectDrivers;
